import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/permissions/usePermissions';
import { primeiraRotaPermitida } from '@/lib/permissions/catalog';
import type { PermissionKey } from '@/lib/permissions/keys';

interface Props {
  /** A chave da tela de entrada (ex.: `inicio.dashboard.view` para `/dashboard`). */
  k: PermissionKey;
  children: ReactNode;
}

/**
 * Porta de entrada que respeita o catálogo: quem tem a chave vê a tela; quem não tem vai para a
 * primeira tela que os seus grupos abrem, em vez de cair numa página que não deveria ver.
 *
 * Diferente de `RequirePermission`, não mostra "Acesso negado": aqui a pessoa não pediu esta
 * tela, o app é que a trouxe (login, troca de org). Sem nenhuma tela permitida, mantém o
 * comportamento anterior e renderiza a tela.
 */
export function EntradaPorPermissao({ k, children }: Props) {
  const { has, loading } = usePermissions();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-64 items-center justify-center text-muted-foreground">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        <span className="ml-2">Verificando permissões…</span>
      </div>
    );
  }

  if (has(k)) return <>{children}</>;

  const destino = primeiraRotaPermitida(has);
  if (!destino || destino === pathname) return <>{children}</>;
  return <Navigate to={destino} replace />;
}
